import { Table, Card, Spin, Empty } from "antd"
import { ISapAssetDetail, ISapImportValidationError } from "@/types"
import { TableOutlined, AppstoreOutlined } from "@ant-design/icons"
import { useState, useEffect, useMemo } from "react"
import moment from "moment"
import SapImportValidationSummary from "@/features/shared/components/SapImportValidationSummary"
import { getAssetColumns } from "../utils/assetColumns"

interface Props {
  items: ISapAssetDetail[]
  loading?: boolean
  error?: string | null
  requiredFields?: string[]
  validationErrors?: ISapImportValidationError[]
  templateKey?: string | null
}

const getPreviewWidth = (label?: string) => {
  const normalized = String(label || "").toLowerCase()

  if (/description|remarks|keterangan/.test(normalized)) return 220
  if (/number|code|class|measure|date|year|price/.test(normalized)) return 150

  return 130
}

const formatPreviewValue = (value: unknown) => {
  if (value === null || value === undefined || value === "") {
    return "-"
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No"
  }

  return String(value)
}

const formatPreviewDate = (value: unknown) => {
  if (!value) {
    return "-"
  }

  const rawValue = String(value)
  const parsedDate = moment(rawValue, ["DD.MM.YYYY", "YYYY-MM-DD", moment.ISO_8601], true)
  return parsedDate.isValid() ? parsedDate.format("DD MMMM YYYY") : rawValue
}

const AssetPreviewCard = ({
  items,
  loading,
  error,
  requiredFields,
  validationErrors,
  templateKey = null,
}: Props) => {
  const [isMobile, setIsMobile] = useState(false)
  const previewColumns = useMemo(
    () =>
      getAssetColumns(templateKey, items).map(column => ({
        title: column.label,
        dataIndex: column.key,
        key: column.key,
        width: getPreviewWidth(column.label),
        ellipsis: true,
        onHeaderCell: () => ({
          style: {
            whiteSpace: "nowrap",
            lineHeight: 1.2,
            padding: "8px 10px",
            fontSize: 12,
          },
        }),
        render: ["asset_capitalization_date", "depreciation_calculation_start_date", "start_use_date"].includes(column.key)
          ? formatPreviewDate
          : undefined,
      })),
    [templateKey, items],
  )
  const desktopColumns = useMemo(
    () => previewColumns.map(col => ({
      ...col,
      render: col.render || ((text: unknown) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {formatPreviewValue(text)}
        </span>
      )),
    })),
    [previewColumns],
  )

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768)
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  return (
    <Card title={<>{isMobile ? <AppstoreOutlined /> : <TableOutlined />} Preview Data from Excel</>} style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }} bodyStyle={{ padding: 0 }}>
      {error && <div style={{ color: "red", margin: 16, padding: 16, background: "#fff1f0", border: "1px solid #ffa39e", borderRadius: 8 }}>{error}</div>}

      <Spin spinning={!!loading}>
        <SapImportValidationSummary requiredFields={requiredFields} validationErrors={validationErrors} />
        {(!items || items.length === 0) && !loading ? (
          <Empty description="No data to preview" style={{ margin: 32 }} />
        ) : (
          <>
            {isMobile ? (
              <div style={{ padding: 16, background: "#fafafa", maxHeight: "500px", overflowY: "auto" }}>
                {items.map((item, index) => (
                    <Card key={index} size="small" style={{ marginBottom: 16, borderRadius: 8, border: "1px solid #f0f0f0" }}>
                    {previewColumns.map((col, idx) => (
                      <div
                        key={col.key}
                        style={{
                          marginBottom: idx === previewColumns.length - 1 ? 0 : 8,
                          borderBottom: idx === previewColumns.length - 1 ? "none" : "1px dashed #f0f0f0",
                          paddingBottom: idx === previewColumns.length - 1 ? 0 : 8,
                        }}
                      >
                        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>{col.title}</div>
                        <div style={{ fontWeight: 500, color: "#262626", wordBreak: "break-word" }}>
                          {["asset_capitalization_date", "depreciation_calculation_start_date", "start_use_date"].includes(col.key)
                            ? formatPreviewDate(item[col.key])
                            : formatPreviewValue(item[col.key])}
                        </div>
                      </div>
                    ))}
                  </Card>
                ))}
              </div>
            ) : (
              <Table
                columns={desktopColumns}
                dataSource={items}
                loading={loading}
                rowKey={(row, index) => row._id || row.record_id || index?.toString() || "0"}
                pagination={{ pageSize: 5, showSizeChanger: false }}
                scroll={{ x: true }}
                tableLayout="fixed"
                size="small"
                style={{ borderTop: "1px solid #f0f0f0" }}
              />
            )}
          </>
        )}
      </Spin>
    </Card>
  )
}

export default AssetPreviewCard
